// components/VehicleAccesMapDialog.jsx
import { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TransferListTable from '../../../components/TransferListTable';
import { fleetApi } from '../../../api/axiosClient';
import { updateVehicleAccess } from '../../../stores/vehicleAccessStore';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function idsFrom(items = []) {
  return items.map((i) => String(i?.id ?? i)).join(',');
}

const normalize = (row) => {
  const id = String(row?.vehicle_access ?? row?.key ?? row?.vehicle_id ?? row?.id ?? row?.value ?? '');
  return {
    id,
    license_plate: row?.vehicle_license_plate ?? row?.vehicle_license_plate ?? row?.label ?? '',
    vehicle_type: row?.vehicle_type ?? row?.vehicle_type ?? '',
    name: row?.vehicle_name ?? row?.vehicle_name ?? '',
    company: row?.vehicle_company ?? row?.vehicle_company ?? '',
    imei: row?.vehicle_imei ?? row?.vehicle_imei ?? '',
    _raw: row
  };
};

const VehicleAccesMapDialog = forwardRef(function VehicleAccesMapDialog({ userRow, onDirtyChange, onSaved }, ref) {
  const { t } = useLanguage();
  const userId = userRow?.id ?? userRow?.user_id ?? null;
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchAssignList = useCallback(
    async (uid, limit = 50, offset = 0) => {
      if (uid == null) return;
      setLoading(true);
      setError(null);
      try {
        const url = `/assign-vehicle-access/list?userId=${encodeURIComponent(uid)}&limit=${limit}&offset=${offset}`;
        const resp = await fleetApi.get(url + getTzParam());
        const payload = resp?.data ?? resp;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.result)
              ? payload.result
              : (payload?.rows ?? []);

        const all = (rows || []).map(normalize).filter((it) => it.id);
        const assigned = [];
        const assignedIds = new Set();

        (rows || []).forEach((r) => {
          const id = String(r?.vehicle_access ?? r?.key ?? r?.vehicle_id ?? r?.id ?? r?.value ?? '');
          const isSelected =
            r?.selected === true ||
            (typeof r?.selected === 'string' && r.selected.toLowerCase() === 'true') ||
            (typeof r?.status === 'string' && r.status.toLowerCase().includes('assign')) ||
            Boolean(r?.selected);
          if (isSelected) {
            const found = all.find((a) => a.id === id);
            if (found) {
              assigned.push(found);
              assignedIds.add(id);
            } else {
              const n = normalize(r);
              if (n.id) {
                assigned.push(n);
                assignedIds.add(n.id);
              }
            }
          }
        });

        const available = all.filter((a) => !assignedIds.has(a.id));

        setLeftItems((prev) => (idsFrom(prev) === idsFrom(available) ? prev : available));
        setRightItems((prev) => (idsFrom(prev) === idsFrom(assigned) ? prev : assigned));
        setDirty(false);
        if (typeof onDirtyChange === 'function') onDirtyChange(false);
      } catch (err) {
        setError(err?.message ?? t('masterUser.error1', 'Failed to retrieve data'));
      } finally {
        setLoading(false);
      }
    },
    [onDirtyChange, t]
  );

  useEffect(() => {
    if (userId == null) {
      setLeftItems([]);
      setRightItems([]);
      return;
    }
    fetchAssignList(userId, 1000, 0);
  }, [userId, fetchAssignList]);

  const columns = useMemo(
    () => [
      { field: 'license_plate', label: t('masterVehicle.licensePlate', 'License Plate') },
      { field: 'vehicle_type', label: t('general.vehicleType', 'Vehicle Type'), render: (it) => it.vehicle_type ?? '-' },
      { field: 'name', label: t('masterVehicle.bodyName', 'Body Name'), render: (it) => it.name ?? '-' },
      { field: 'company', label: t('general.company', 'Company'), render: (it) => it.company ?? '-' },
      { field: 'imei', label: 'IMEI', render: (it) => it.imei ?? '-' }
    ],
    [t]
  );

  const handleChange = useCallback(
    ({ left = [], right = [] }) => {
      const leftObjs = (left || []).map((id) => {
        const found = leftItems.find((c) => String(c.id) === String(id)) || rightItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });
      const rightObjs = (right || []).map((id) => {
        const found = rightItems.find((c) => String(c.id) === String(id)) || leftItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });
      setLeftItems(leftObjs);
      setRightItems(rightObjs);
      setDirty(true);
      if (typeof onDirtyChange === 'function') onDirtyChange(true);
    },
    [leftItems, rightItems, onDirtyChange]
  );

  const save = useCallback(async () => {
    if (!userId) throw new Error('userId is required');
    setSaving(true);
    setError(null);
    try {
      const ids = (rightItems || []).map((c) => Number(c.id));
      await updateVehicleAccess(userId, ids);
      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);
      if (typeof onSaved === 'function') onSaved({ userId, assigned: ids });
      await fetchAssignList(userId, 1000, 0);
      return { success: true, assigned: ids };
    } catch (err) {
      setError(err?.message ?? t('masterUser.error2', 'Failed saving data'));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [userId, rightItems, onDirtyChange, onSaved, fetchAssignList, t]);

  useImperativeHandle(
    ref,
    () => ({
      save,
      isDirty: () => dirty,
      getAssignedIds: () => (rightItems || []).map((c) => String(c.id ?? c))
    }),
    [save, dirty, rightItems]
  );

  if (!userRow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">{t('masterUser.subtitle2', 'Select a user to view Vehicle Access')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading || saving ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <TransferListTable
            leftItems={leftItems}
            rightItems={rightItems}
            onChange={handleChange}
            titleLeft={t('masterUser.avaibleVehicle', 'Available Vehicle')}
            titleRight={t('masterUser.choosenVehicle', 'Choosen Vehicle')}
            itemKey="id"
            itemLabel="name"
            showSearch
            columns={columns}
          />
        </>
      )}
    </Box>
  );
});

export default VehicleAccesMapDialog;
